/**
 * Medien & Dokumente eines PB/BK (CRM 2.0, v2 admin-scoped).
 *
 * Backend-Vertrag (`{ data, meta, errors }`-Envelope, Guard 'admin'):
 *
 *   GET    /v2/admin/users/{id}/media-documents → { media[], documents[] } + meta
 *                                                  (Kategorien je Rolle, Limits)
 *   POST   /v2/admin/users/{id}/media           — multipart files[] + kategorie
 *   POST   /v2/admin/users/{id}/documents       — multipart files[] + kategorie
 *   PATCH  /v2/admin/media/{id} | /documents/{id} — { kategorie?, name? }
 *   DELETE /v2/admin/media/{id} | /documents/{id}
 *
 * Die Dateien liegen privat im Hetzner Object Storage. `url`, `download_url`
 * und `thumbnail_url` sind kurzlebige (15 min) presigned bzw. signierte URLs —
 * <img>/<video>/<iframe> brauchen also keinen Bearer-Token. Die Liste wird
 * deshalb regelmäßig neu geladen (siehe useMediaDocuments).
 */
import { apiV2 } from './api';

export type MediaDocKind = 'media' | 'document';

export interface MediaDocUploader {
  type: 'admin' | 'customer' | 'system';
  id: number | null;
  name: string | null;
}

export interface MediaDocItem {
  id: number;
  type: MediaDocKind;
  user_id: number;
  kategorie: string;
  name: string;
  mime: string;
  size: number;
  is_image: boolean;
  is_video: boolean;
  is_pdf: boolean;
  /** Video wird noch transkodiert — noch keine URL. */
  processing: boolean;
  storage: 'cloud' | 'local';
  uploaded_by: MediaDocUploader | null;
  created_at: string | null;
  url: string | null;
  download_url: string | null;
  thumbnail_url: string | null;
}

export interface MediaDocLimits {
  image_max_kb: number;
  video_max_kb: number;
  document_max_kb: number;
  files_per_request: number;
}

export interface MediaDocMeta {
  role: 'pb' | 'bk';
  categories: { media: string[]; documents: string[] };
  limits: MediaDocLimits;
  accept: { media: string[]; documents: string[] };
  storage: 'cloud' | 'local';
}

export interface MediaDocumentsData {
  media: MediaDocItem[];
  documents: MediaDocItem[];
  meta: MediaDocMeta;
}

/** Fallbacks, solange `meta` noch nicht geladen ist (spiegelt config/media.php). */
export const DEFAULT_MEDIA_DOC_META: MediaDocMeta = {
  role: 'pb',
  categories: {
    media: ['person', 'wohnung', 'video'],
    documents: ['vertrag', 'rechnung', 'mahnung', 'sonstiges'],
  },
  limits: { image_max_kb: 20480, video_max_kb: 204800, document_max_kb: 25600, files_per_request: 20 },
  accept: {
    media: ['jpg', 'jpeg', 'png', 'webp', 'heic', 'heif', 'mp4', 'webm', 'mov', 'qt', 'm4v'],
    documents: ['pdf', 'doc', 'docx', 'xls', 'xlsx', 'odt', 'ods', 'txt', 'csv', 'jpg', 'jpeg', 'png', 'webp', 'heic', 'heif'],
  },
  storage: 'cloud',
};

/** Uploads dürfen deutlich länger dauern als die 15 s Standard-Timeout von apiV2. */
const UPLOAD_TIMEOUT_MS = 30 * 60 * 1000;

const kindPath = (kind: MediaDocKind) => (kind === 'media' ? 'media' : 'documents');

export const mediaDocumentsApi = {
  list: async (userId: number): Promise<MediaDocumentsData> => {
    const res = await apiV2.get<{ data: { media: MediaDocItem[]; documents: MediaDocItem[] }; meta: MediaDocMeta }>(
      `/admin/users/${userId}/media-documents`,
    );
    return {
      media: res.data.data?.media ?? [],
      documents: res.data.data?.documents ?? [],
      meta: { ...DEFAULT_MEDIA_DOC_META, ...(res.data.meta ?? {}) },
    };
  },

  /** Lädt Dateien einer Art + Kategorie hoch; `onProgress` liefert 0–100. */
  upload: async (
    userId: number,
    kind: MediaDocKind,
    files: File[],
    kategorie: string,
    onProgress?: (percent: number) => void,
  ): Promise<MediaDocItem[]> => {
    const fd = new FormData();
    files.forEach(f => fd.append('files[]', f));
    fd.append('kategorie', kategorie);
    const res = await apiV2.post<{ data: MediaDocItem[] }>(`/admin/users/${userId}/${kindPath(kind)}`, fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: UPLOAD_TIMEOUT_MS,
      onUploadProgress: e => {
        if (!onProgress) return;
        const total = e.total ?? files.reduce((s, f) => s + f.size, 0);
        onProgress(total > 0 ? Math.min(100, Math.round((e.loaded / total) * 100)) : 0);
      },
    });
    return res.data.data ?? [];
  },

  update: async (kind: MediaDocKind, id: number, patch: { kategorie?: string; name?: string }): Promise<MediaDocItem> => {
    const res = await apiV2.patch<{ data: MediaDocItem }>(`/admin/${kindPath(kind)}/${id}`, patch);
    return res.data.data;
  },

  remove: async (kind: MediaDocKind, id: number): Promise<void> => {
    await apiV2.delete(`/admin/${kindPath(kind)}/${id}`);
  },
};

/* ─── Helfer ─────────────────────────────────────────────────────────────── */

export function fileExtension(name: string): string {
  const i = name.lastIndexOf('.');
  return i >= 0 ? name.slice(i + 1).toLowerCase() : '';
}

const VIDEO_EXT = new Set(['mp4', 'webm', 'mov', 'qt', 'm4v']);
const IMAGE_EXT = new Set(['jpg', 'jpeg', 'png', 'webp', 'heic', 'heif', 'gif']);

export function isVideoFile(file: File): boolean {
  return file.type.startsWith('video/') || VIDEO_EXT.has(fileExtension(file.name));
}

export function isImageFile(file: File): boolean {
  return file.type.startsWith('image/') || IMAGE_EXT.has(fileExtension(file.name));
}

/** Bilder/Videos → Medien, alles andere → Dokumente. */
export function detectKind(file: File): MediaDocKind {
  return isImageFile(file) || isVideoFile(file) ? 'media' : 'document';
}

/** Welche Arten kommen für die Datei in Frage (Bilder: beide). */
export function allowedKinds(file: File, meta: MediaDocMeta = DEFAULT_MEDIA_DOC_META): MediaDocKind[] {
  const ext = fileExtension(file.name);
  const kinds: MediaDocKind[] = [];
  if (meta.accept.media.includes(ext)) kinds.push('media');
  if (meta.accept.documents.includes(ext)) kinds.push('document');
  return kinds;
}

export function defaultCategory(file: File, kind: MediaDocKind, meta: MediaDocMeta = DEFAULT_MEDIA_DOC_META): string {
  if (kind === 'media') {
    const cats = meta.categories.media;
    if (isVideoFile(file) && cats.includes('video')) return 'video';
    return cats.includes('person') ? 'person' : cats[0] ?? 'person';
  }
  const cats = meta.categories.documents;
  return cats.includes('sonstiges') ? 'sonstiges' : cats[cats.length - 1] ?? 'sonstiges';
}

/** Max. Größe in Bytes für Datei + Art (aus meta.limits). */
export function maxBytesFor(file: File, kind: MediaDocKind, meta: MediaDocMeta = DEFAULT_MEDIA_DOC_META): number {
  const kb = kind === 'document'
    ? meta.limits.document_max_kb
    : isVideoFile(file) ? meta.limits.video_max_kb : meta.limits.image_max_kb;
  return kb * 1024;
}

export function formatFileSize(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes <= 0) return '0 KB';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
  return `${(bytes / 1024 / 1024 / 1024).toFixed(2)} GB`;
}

/** Erste Validierungsmeldung aus einer 422-Antwort (Laravel) oder null. */
export function mediaDocErrorMessage(err: unknown): string | null {
  const e = err as { response?: { data?: { message?: string; errors?: Record<string, string[]> | Array<{ message?: string }> | null } } };
  const errors = e.response?.data?.errors;
  if (errors && !Array.isArray(errors)) {
    const first = Object.values(errors)[0];
    if (Array.isArray(first) && first[0]) return first[0];
  }
  if (Array.isArray(errors) && errors[0]?.message) return errors[0].message;
  return e.response?.data?.message ?? null;
}

/* ─── Geteilter Zustand: Tab + Dashboard-Widgets ─────────────────────────── */

const CHANGED_EVENT = 'sh24:media-documents-changed';

/** Nach Upload/Löschen/Ändern aufrufen — alle Ansichten des Users laden neu. */
export function notifyMediaDocumentsChanged(userId: number): void {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent(CHANGED_EVENT, { detail: { userId } }));
}

export function subscribeMediaDocumentsChanged(userId: number, cb: () => void): () => void {
  if (typeof window === 'undefined') return () => undefined;
  const handler = (e: Event) => {
    const detail = (e as CustomEvent<{ userId: number }>).detail;
    if (detail?.userId === userId) cb();
  };
  window.addEventListener(CHANGED_EVENT, handler);
  return () => window.removeEventListener(CHANGED_EVENT, handler);
}
